<template>
    <a
        class="comment-attachment"
        :class="{
            'comment-attachment_dark': dark,
            'comment-attachment_error': attachment.errorMessage,
            'comment-attachment_with-actions': hasImageActions,
            'comment-attachment_deletable': hasImageActions && allowDeletion,
        }"
        target="_blank"
        rel="noopener"
        :href="attachmentUri"
        @click="downloadAttachment"
        :download="extension !== 'pdf' ? attachment.id + '-' + attachment.name : undefined"
    >
        <template v-if="isBrowsableAttachment">
            <img
                class="comment-attachment__image"
                :src="attachmentUri"
                :alt="attachment.name"
                @click.stop.prevent="showImagePopup()"
                v-tooltip="{ content: 'Открыть на весь экран', classes: tooltipClasses }"
            />
        </template>

        <template v-else>
            <span class="comment-attachment__extension">
                {{ extension }}
            </span>
        </template>

        <span
            class="comment-attachment__label"
            :class="{
                'comment-attachment__label_error': attachment.errorMessage,
            }"
            v-tooltip="{ content: attachment.name, classes: tooltipClasses }"
        >
            {{ attachment.name }}

            <span class="comment-attachment__error-message" v-if="attachment.errorMessage">
                <br />
                {{ attachment.errorMessage }}
            </span>
            <span class="comment-attachment__error-message" v-if="privateError">{{ privateError }}</span>
        </span>

        <!-- Действия с изображением. Кнопки видны всегда, а не по наведению: на телефоне
             наведения нет. Скачивание идёт тем же путём, что и превью, — через API
             обращения, поэтому права проверяет сервер. -->
        <span class="comment-attachment__actions" v-if="hasImageActions">
            <button
                type="button"
                class="comment-attachment__action comment-attachment__action_view"
                aria-label="Просмотреть"
                v-tooltip="{ content: 'Просмотреть', classes: tooltipClasses }"
                @click.stop.prevent="showImagePopup()"
            >
                <EyeIcon :size="18"></EyeIcon>
            </button>

            <button
                type="button"
                class="comment-attachment__action comment-attachment__action_download"
                aria-label="Скачать"
                v-tooltip="{ content: 'Скачать', classes: tooltipClasses }"
                @click.stop.prevent="downloadOriginal()"
            >
                <DownloadIcon :size="18"></DownloadIcon>
            </button>
        </span>

        <VProgressRadial
            class="comment-attachment__progress-bar"
            :progress="attachment.progress"
            v-if="attachment.isUploading"
        ></VProgressRadial>

        <button
            class="comment-attachment__delete-button comment-attachment__retry-button"
            v-if="attachment.retryable"
            v-tooltip="'Повторить загрузку'"
            @click.stop.prevent="$emit('retry', attachment)"
        >
            ↻
        </button>

        <button
            class="comment-attachment__delete-button"
            v-if="(allowDeletion || attachment.errorMessage) && !attachment.isUploading"
            v-tooltip="'Удалить файл'"
            @click.stop.prevent="$emit('delete', attachment)"
        >
            <TimesSvg></TimesSvg>
        </button>
    </a>
</template>
<script>
// Components
import VProgressRadial from '../../../components/VProgress/VProgressRadial.vue';

import TimesSvg from '@/images/times.svg';
import EyeIcon from 'vue-material-design-icons/Eye.vue';
import DownloadIcon from 'vue-material-design-icons/Download.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import http from '@/js/http';

let serverURL = process.env.VUE_APP_SERVER_URL;

if (!serverURL) {
    serverURL = '';
}

if (serverURL.endsWith('/')) {
    serverURL = serverURL.slice(0, -1);
}

const downloadBaseUrl = serverURL + '/files';

// Типы, которые можно показать в браузере. Совпадают с ChatAttachmentTypes::INLINE_TYPES
// на сервере: всё остальное сервер отдаёт как application/octet-stream.
const INLINE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

@Component({
    components: {
        VProgressRadial,

        TimesSvg,
        EyeIcon,
        DownloadIcon,
    },
})
class ChatComment extends Vue {
    privateUrl = '';
    privateType = '';
    privateError = '';
    @Prop({ type: Boolean, default: false })
    dark;

    @Prop({ type: Boolean, default: false })
    allowDeletion;

    @Prop({ type: Object, required: true })
    attachment;

    async showImagePopup() {
        if (this.attachment.requestId && !this.attachment.dataURL) {
            try {
                await this.loadPrivateBlob();
            } catch (error) {
                this.privateError = 'Не удалось открыть файл';
                return;
            }
        }
        if (!this.attachmentUri) return;
        this.privateError = '';
        this.$bus.emit('ImagePopup:Show', this.attachmentUri);
    }

    /**
     * Скачать оригинал изображения, а не открыть его: сервер отдаёт картинки inline,
     * поэтому имя и сохранение задаются ссылкой с атрибутом download.
     */
    async downloadOriginal() {
        if (!this.hasImageActions) return;
        try {
            const url = this.attachment.requestId ? await this.loadPrivateBlob() : this.attachmentUri;
            const link = document.createElement('a');
            link.href = url;
            link.download = this.attachment.name;
            if (!this.attachment.requestId) {
                // Старая публичная ссылка может вести на другой домен, где download
                // игнорируется, — тогда пусть откроется рядом, а не вместо обращения.
                link.target = '_blank';
                link.rel = 'noopener';
            }
            link.click();
            this.privateError = '';
        } catch (error) {
            this.privateError = 'Не удалось скачать файл';
        }
    }

    async loadPrivateBlob() {
        if (this.privateUrl) return this.privateUrl;
        const response = await http.get(`/requests/${this.attachment.requestId}/files/${this.attachment.id}`, {
            responseType: 'blob',
        });
        // Тип blob задаём сами, а не берём как есть: blob-адрес живёт в происхождении сайта,
        // и blob с типом text/html открылся бы страницей сервис-деска со своими скриптами.
        const type = String(response.data.type || '')
            .split(';')[0]
            .trim()
            .toLowerCase();
        this.privateType = INLINE_TYPES.includes(type) ? type : 'application/octet-stream';
        this.privateUrl = URL.createObjectURL(new Blob([response.data], { type: this.privateType }));
        return this.privateUrl;
    }

    async downloadAttachment(event) {
        if (!this.attachment.requestId) return;
        event.preventDefault();
        if (this.attachment.isUploading || this.attachment.errorMessage || !this.attachment.id || this.attachment.id < 1) return;
        // Окно открывается сразу, в обработчике клика, иначе его заблокирует браузер.
        // Без opener: открытый документ не должен дотянуться до вкладки сервис-деска.
        const isPdf = this.extension?.toLowerCase() === 'pdf' && this.attachment.type === 'application/pdf';
        const pdfWindow = isPdf ? window.open('about:blank', '_blank') : null;
        if (pdfWindow) pdfWindow.opener = null;
        try {
            const url = await this.loadPrivateBlob();
            if (pdfWindow && this.privateType === 'application/pdf') {
                pdfWindow.location.href = url;
            } else {
                // Сервер не подтвердил PDF — файл только скачивается.
                if (pdfWindow) pdfWindow.close();
                const link = document.createElement('a');
                link.href = url;
                link.download = this.attachment.name;
                link.click();
            }
        } catch (error) {
            if (pdfWindow) pdfWindow.close();
            this.privateError = 'Не удалось скачать файл';
        }
    }

    mounted() {
        if (this.attachment.requestId && this.isBrowsableAttachment && !this.attachment.dataURL) {
            this.loadPrivateBlob().catch(() => {
                this.privateError = 'Не удалось открыть файл';
            });
        }
    }

    beforeDestroy() {
        if (this.privateUrl) URL.revokeObjectURL(this.privateUrl);
    }

    get extension() {
        if (this.attachment?.name) {
            const fragments = this.attachment.name.split('.');
            const extension = fragments[fragments.length - 1];

            return extension;
        }

        return undefined;
    }

    get attachmentUri() {
        if (this.attachment.dataURL) {
            return this.attachment.dataURL;
        }

        if (this.attachment.requestId) return this.privateUrl;
        return this.attachment.publicId ? `${downloadBaseUrl}/${this.attachment.publicId}` : '';
    }

    get tooltipClasses() {
        return this.dark ? 'tooltip-light' : '';
    }

    get isBrowsableAttachment() {
        return this.attachment.type.match(/^image\/*/);
    }

    /**
     * Кнопки «Просмотреть» и «Скачать» — только для изображения, которое уже есть
     * на сервере: пока файл грузится или загрузка сорвалась, скачивать нечего.
     */
    get hasImageActions() {
        if (!this.isBrowsableAttachment || this.attachment.isUploading || this.attachment.errorMessage) {
            return false;
        }

        if (this.attachment.requestId) return this.attachment.id > 0;

        return !!this.attachmentUri;
    }
}

export default ChatComment;
</script>
<style lang="scss">
:root {
    --comment-attachment-color: #00a75c;
    --comment-attachment-color-dark: #ffffff;

    --comment-attachment-background: #f2f5f9;
    --comment-attachment-background-dark: #383838;

    --comment-attachment-extension-background: var(--color-green);
    --comment-attachment-extension-background-error: #dedddd;
}

.comment-attachment {
    position: relative;
    display: flex;
    // min-height, а не height: в крупном размере текста строка имени с отступами
    // выше 64px, и с фиксированной высотой имя вылезало бы за нижний край плашки.
    min-height: 64px;
    max-width: 330px;
    color: var(--comment-attachment-color);
    background: var(--comment-attachment-background);
    cursor: pointer;
    border-radius: 10px;

    &_dark {
        color: var(--comment-attachment-color-dark);
        background: var(--comment-attachment-background-dark);
    }

    &_error {
        color: var(--error-color);

        .comment-attachment__label {
            margin: 16px 48px 16px 10px;
        }

        .comment-attachment__extension {
            background: var(--comment-attachment-extension-background-error);
        }
    }

    &__image,
    &__extension {
        flex: 0 0 56px;
        width: 56px;
        height: 56px;
        min-height: 56px;
        max-height: 56px;
        border-radius: 10px;
        margin: 4px;
    }

    &__extension {
        display: inline-block;
        font-size: 1.125rem;
        color: var(--color-white);
        // По высоте плашки расширения, а не от размера текста: в rem строка в крупном
        // режиме выходила выше 56px, и подпись уезжала вниз под overflow: hidden.
        line-height: 56px;
        text-align: center;
        text-transform: uppercase;
        user-select: none;
        overflow: hidden;
        white-space: nowrap;
        background: var(--comment-attachment-extension-background);
    }

    &__image {
        width: 64px;
        object-fit: cover;
        overflow: hidden;
    }

    &__label {
        display: inline-block;
        margin: 24px 48px 24px 10px;
        max-width: 100%;
        line-height: 1rem;
        font-size: 0.875rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__error-message {
        line-height: 0.875rem;
        font-size: 0.75rem;
    }

    &__progress-bar,
    &__delete-button {
        position: absolute;
        top: 0;
        right: 0;
        width: 28px;
        height: 28px;
        margin: 0;
        padding: 8px;
    }

    &__delete-button {
        display: flex;

        & svg {
            fill: var(--error-color);
            width: 12px;
            height: 12px;
        }
    }

    &__retry-button {
        right: 30px;
    }

    &__progress-bar {
        padding: 6px;
    }

    &_with-actions &__label {
        flex: 1 1 auto;
        min-width: 0;
        margin-right: 4px;
    }

    &__actions {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        gap: 2px;
        margin-right: 6px;
    }

    &_deletable &__actions {
        margin-right: 28px;
    }

    &__action {
        display: flex;
        width: 32px;
        height: 32px;
        margin: 0;
        padding: 0;
        align-items: center;
        justify-content: center;
        color: inherit;
        background: transparent;
        border: none;
        border-radius: 8px;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            background: rgba(0, 0, 0, 0.08);
        }
    }

    &_dark &__action {
        &:hover,
        &:focus-visible {
            background: rgba(255, 255, 255, 0.16);
        }
    }
}

@media (max-width: $mobile-breakpoint) {
    .comment-attachment__action {
        width: 40px;
        height: 40px;
    }
}
</style>
